import React from "react";
import {
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  alpha,
  ListItemIcon,
  ListItemText,
  styled,
} from "@mui/material";
import {
  ManageAccounts as ManageAccountsIcon,
  VpnKey as VpnKeyIcon,
  AppSettingsAlt as AppSettingsAltIcon,
  TimerOff as TimerOffIcon,
  PanTool as PanToolIcon,
  SystemUpdateAlt as SystemUpdateAltIcon,
  CurrencyExchangeRounded as CurrencyExchangeRoundedIcon,
  PublishedWithChanges as PublishedWithChangesIcon,
  AccountBalanceWallet as WalletIcon,
  MoreTime as MoreTimeIcon,
  Receipt as ReceiptIcon,
  ShowChart as ShowChartIcon,
  Visibility as VisibilityIcon,
  Dvr as DvrIcon,
  Edit as EditIcon,
  Refresh,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
} from "@mui/icons-material";

const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: "16px",
    marginTop: "8px",
    minWidth: 220,
    boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    padding: "4px",
    background: alpha(theme.palette.background.paper, 0.98),
    backdropFilter: "blur(10px)",
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme, color }) => ({
  borderRadius: "10px",
  padding: "10px 16px",
  margin: "2px 4px",
  gap: "12px",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette[color || "primary"].main, 0.08),
    "& .MuiListItemIcon-root": {
      color: theme.palette[color || "primary"].main,
      transform: "scale(1.1)",
    },
    "& .MuiListItemText-primary": {
      color: theme.palette[color || "primary"].main,
    },
  },
  "& .MuiListItemIcon-root": {
    minWidth: "auto",
    color: color ? theme.palette[color].main : theme.palette.text.secondary,
    transition: "all 0.2s ease",
  },
  "& .MuiListItemText-primary": {
    fontWeight: 700,
    fontSize: "0.825rem",
    transition: "all 0.2s ease",
  },
}));

const ActionMenu = ({
  anchorEl,
  onClose,
  permissionKeys,
  user,
  operator,
  theme,
  handlers,
  type,
}) => {
  const {
    changeUserName,
    changeRouterPass,
    changeAppPass,
    suspenduser,
    holdClick,
    updateDataLimit,
    changePlan,
    assignPlan,
    renewClick,
    addWallet,
    changeValidityClick,
    invoiceClick,
    disconnectUser,
    showClick,
    showRouterClick,
    showAppClick,
    caRenewal,
    staticIp,
    editUser,
    refreshPage,
  } = handlers;

  const open = Boolean(anchorEl);

  const renderAccountItems = () => (
    <>
      {(permissionKeys?.includes("change_user_name") ||
        operator?.businessType === "SUPER_ADMIN" ||
        operator?.businessType === "ADMIN") && (
        <StyledMenuItem
          onClick={() => {
            changeUserName();
            onClose();
          }}
        >
          <ListItemIcon>
            <ManageAccountsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Username</ListItemText>
        </StyledMenuItem>
      )}
      {permissionKeys?.includes("change_router_password") && (
        <StyledMenuItem
          onClick={() => {
            changeRouterPass();
            onClose();
          }}
        >
          <ListItemIcon>
            <VpnKeyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Router Password</ListItemText>
        </StyledMenuItem>
      )}
      {permissionKeys?.includes("change_app_password") && (
        <StyledMenuItem
          onClick={() => {
            changeAppPass();
            onClose();
          }}
        >
          <ListItemIcon>
            <AppSettingsAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>App Password</ListItemText>
        </StyledMenuItem>
      )}
      <Divider sx={{ my: 1, opacity: 0.5 }} />
      {permissionKeys?.includes("suspend") && (
        <StyledMenuItem
          color="error"
          onClick={() => {
            suspenduser();
            onClose();
          }}
        >
          <ListItemIcon>
            <TimerOffIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Suspend User</ListItemText>
        </StyledMenuItem>
      )}
      <StyledMenuItem
        onClick={() => {
          holdClick();
          onClose();
        }}
      >
        <ListItemIcon>
          <PanToolIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Hold User</ListItemText>
      </StyledMenuItem>
      {(permissionKeys?.includes("update_data_limit") ||
        operator?.businessType === "MULTI_SERVICE_OPERATOR" ||
        operator?.businessType === "SUPER_ADMIN" ||
        operator?.businessType === "ADMIN") && (
        <StyledMenuItem
          onClick={() => {
            updateDataLimit();
            onClose();
          }}
        >
          <ListItemIcon>
            <SystemUpdateAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Update Data Limit</ListItemText>
        </StyledMenuItem>
      )}
    </>
  );

  const renderBillingItems = () => (
    <>
      {permissionKeys?.includes("change_plan") && (
        <StyledMenuItem
          onClick={() => {
            changePlan();
            onClose();
          }}
        >
          <ListItemIcon>
            <CurrencyExchangeRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Plan</ListItemText>
        </StyledMenuItem>
      )}
      {(permissionKeys?.includes("assign_plan") ||
        operator?.businessType === "SUPER_ADMIN" ||
        operator?.businessType === "ADMIN") && (
        <StyledMenuItem
          onClick={() => {
            assignPlan();
            onClose();
          }}
        >
          <ListItemIcon>
            <AssignmentTurnedInIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Assign Plan</ListItemText>
        </StyledMenuItem>
      )}
      {permissionKeys?.includes("renew") && (
        <StyledMenuItem
          onClick={() => {
            renewClick();
            onClose();
          }}
        >
          <ListItemIcon>
            <PublishedWithChangesIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Renew Account</ListItemText>
        </StyledMenuItem>
      )}
      {permissionKeys?.includes("wallet") && (
        <StyledMenuItem
          onClick={() => {
            addWallet();
            onClose();
          }}
        >
          <ListItemIcon>
            <WalletIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Wallet Recharge</ListItemText>
        </StyledMenuItem>
      )}
      {permissionKeys?.includes("change_validity") && (
        <StyledMenuItem
          onClick={() => {
            changeValidityClick();
            onClose();
          }}
        >
          <ListItemIcon>
            <MoreTimeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Validity</ListItemText>
        </StyledMenuItem>
      )}
      {permissionKeys?.includes("proforma_invoices") && (
        <StyledMenuItem
          onClick={() => {
            invoiceClick();
            onClose();
          }}
        >
          <ListItemIcon>
            <ReceiptIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Proforma Invoice</ListItemText>
        </StyledMenuItem>
      )}
      {(permissionKeys?.includes("auto_renewal") ||
        operator?.businessType === "SUPER_ADMIN" ||
        operator?.businessType === "ADMIN") && (
        <StyledMenuItem
          onClick={() => {
            caRenewal();
            onClose();
          }}
        >
          <ListItemIcon>
            <PublishedWithChangesIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Auto Renewal</ListItemText>
        </StyledMenuItem>
      )}
    </>
  );

  const renderToolsItems = () => (
    <>
      {user.connectionStatus === "online" && permissionKeys?.includes("disconnect_user") && (
        <StyledMenuItem
          color="error"
          onClick={() => {
            disconnectUser();
            onClose();
          }}
        >
          <ListItemIcon>
            <TimerOffIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Disconnect User</ListItemText>
        </StyledMenuItem>
      )}
      <StyledMenuItem
        onClick={() => {
          showClick();
          onClose();
        }}
      >
        <ListItemIcon>
          <ShowChartIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Speed Graph</ListItemText>
      </StyledMenuItem>
      {(permissionKeys?.includes("show_router_pwd") ||
        operator?.businessType === "SUPER_ADMIN" ||
        operator?.businessType === "ADMIN") && (
        <StyledMenuItem
          onClick={() => {
            showRouterClick();
            onClose();
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Router Pwd</ListItemText>
        </StyledMenuItem>
      )}
      {(permissionKeys?.includes("show_app_password") ||
        operator?.businessType === "SUPER_ADMIN" ||
        operator?.businessType === "ADMIN" ||
        operator?.businessType === "MULTI_SERVICE_OPERATOR" ||
        operator?.businessType === "LOCAL_CABLE_OPERATOR") && (
        <StyledMenuItem
          onClick={() => {
            showAppClick();
            onClose();
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View App Pwd</ListItemText>
        </StyledMenuItem>
      )}
      {operator &&
        (operator.businessType === "MULTI_SERVICE_OPERATOR" ||
          operator.businessType === "SUPER_ADMIN") && (
          <StyledMenuItem
            onClick={() => {
              staticIp();
              onClose();
            }}
          >
            <ListItemIcon>
              <DvrIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Static IP</ListItemText>
          </StyledMenuItem>
        )}
      {permissionKeys?.includes("edit") && (
        <StyledMenuItem
          onClick={() => {
            editUser();
            onClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit User Details</ListItemText>
        </StyledMenuItem>
      )}
      <Divider sx={{ my: 1, opacity: 0.5 }} />
      <StyledMenuItem
        onClick={() => {
          refreshPage();
          onClose();
        }}
      >
        <ListItemIcon>
          <Refresh fontSize="small" />
        </ListItemIcon>
        <ListItemText>Refresh Data</ListItemText>
      </StyledMenuItem>
    </>
  );

  return (
    <StyledMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      {type === "account" && renderAccountItems()}
      {type === "billing" && renderBillingItems()}
      {type === "tools" && renderToolsItems()}
    </StyledMenu>
  );
};

export default ActionMenu;
